import React from 'react';
import SectionContainer from './SectionContainer';
import MetricDisplay from './MetricDisplay';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

function EnvironmentalSection({ data, nationalAvg, utilityData, selectedYear }) {
  if (!data) {
    return (
      <SectionContainer icon="&#x1F4CA;" title="Environmental Performance">
        <p className="text-sm text-gray-400">No environmental data available</p>
      </SectionContainer>
    );
  }

  // Prepare generation mix data for pie chart
  const generationMix = [
    { name: 'Renewable', value: data.renewable_pct || 0, color: '#10b981' },
    { name: 'Natural Gas', value: data.gas_pct || 0, color: '#6366f1' },
    { name: 'Coal', value: data.coal_pct || 0, color: '#374151' },
    { name: 'Nuclear', value: data.nuclear_pct || 0, color: '#f59e0b' },
    { name: 'Other', value: data.other_pct || 0, color: '#9ca3af' },
  ].filter(item => item.value > 0);

  return (
    <SectionContainer icon="&#x1F4CA;" title="Environmental Performance">
      <div className="space-y-1 divide-y divide-gray-100">
        <MetricDisplay
          label="Renewable Energy"
          value={data.renewable_pct}
          unit="%"
          nationalAvg={nationalAvg?.renewable_pct}
          lowerIsBetter={false}
          precision={1}
        />

        <MetricDisplay
          label="CO2 per Customer"
          value={data.co2_per_customer}
          unit=" tons/yr"
          nationalAvg={nationalAvg?.co2_per_customer}
          lowerIsBetter={true}
          precision={1}
        />
      </div>

      {/* Generation Mix Pie Chart */}
      {generationMix.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Generation Mix</h4>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={generationMix}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {generationMix.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => `${value.toFixed(1)}%`}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '12px'
                  }}
                />
                <Legend
                  layout="horizontal"
                  verticalAlign="bottom"
                  align="center"
                  wrapperStyle={{ fontSize: '10px' }}
                  formatter={(value) => <span className="text-gray-600">{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Detailed breakdown */}
      <div className="mt-3 pt-3 border-t border-gray-100">
        <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Generation Breakdown</h4>
        <div className="space-y-2">
          {generationMix.map((item) => (
            <div key={item.name} className="flex items-center">
              <div
                className="w-3 h-3 rounded-full mr-2"
                style={{ backgroundColor: item.color }}
              />
              <span className="flex-1 text-sm text-gray-600">{item.name}</span>
              <span className="text-sm font-medium text-gray-800">{item.value.toFixed(1)}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* Environmental rating */}
      {data.renewable_pct && (
        <div className="mt-3 p-3 rounded-lg bg-gradient-to-r from-green-50 to-blue-50">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Clean Energy Score</span>
            <div className="flex items-center">
              {/* Star rating based on renewable % */}
              {[1, 2, 3, 4, 5].map((star) => {
                const threshold = star * 20; // 20%, 40%, 60%, 80%, 100%
                const isFilled = data.renewable_pct >= threshold - 10;
                return (
                  <svg
                    key={star}
                    className={`w-4 h-4 ${isFilled ? 'text-yellow-400' : 'text-gray-300'}`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                );
              })}
            </div>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Based on {data.renewable_pct?.toFixed(0)}% renewable energy generation
          </p>
        </div>
      )}

      {/* Data source with estimation warning */}
      <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
        <div className="flex items-start">
          <svg className="w-4 h-4 mr-1.5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div>
            <span className="font-medium">Estimated Data:</span> Environmental metrics are estimated from state-level eGRID regional averages, not utility-specific reported values.
          </div>
        </div>
      </div>
    </SectionContainer>
  );
}

export default EnvironmentalSection;
